import React, { useContext, useEffect } from "react";
import FilterPopup from "../FilterPopup";
import { Button, Message } from "semantic-ui-react";
import Tag from "components/Tag/Tag";
import ButtonGroup from "components/Atoms/ButtonGroup";
import { type TagFilterType, getSortedTags, initialTagFilter } from "./FilterTypes/TagFilterType";
import { routes } from "utilities/routing/generatedRoutes";
import { splitIntoTwoColumns } from "utilities";
import { focusOnMount } from "utilities/reactUtils";
import TagList from "components/Tag/TagList";
import { FilterContext } from "../FilterContext";
import { areArrayValuesEqual } from "../FilterGroups/instructionUtils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { VerticalKeyboardNavigationGroup } from "keyboardNavigation/KeyboardNavigationPresets";

interface Props {
  mainArea: server.dto.MainAreas
  initialTags: server.dto.TagSmall[]
}

const TagFilter = (props: Props) => {
  const withoutTagTitle = RESX.Tag.WithoutTag;
  const withTagTitle = RESX.Tag.WithTag;

  const { register, getValue, setInitialValue, setValue } = useContext(FilterContext);
  const reset = (): TagFilterType => ({
    selectedTags: [],
    initialTags: props.initialTags,
    filterType: "in"
  });

  register("tags", {
    getTitle: (filter) => {
      if (filter.filterType === "in") { return withTagTitle; } else { return withoutTagTitle; }
    },
    renderSelection: (filter) => {
      return <TagList tags={getSortedTags(filter)} version="long" />;
    },
    hasValue: (filter) => {
      return filter.selectedTags.length > 0;
    },
    reset,
    areEqual: (a, b) => {
      return a.filterType === b.filterType && areArrayValuesEqual(a.selectedTags.map(t => t.tagId), b.selectedTags.map(t => t.tagId));
    }
  });
  useEffect(() => {
    setInitialValue("tags", reset());
  }, []);

  const [filterType, setFilterType] = React.useState<server.dto.WhereInOrNotIn>(initialTagFilter.filterType);
  const [selectedTags, setSelectedTags] = React.useState<server.dto.TagSmall[]>(initialTagFilter.selectedTags);
  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);

  const onPopupClose = () => {
    onApply();
  };

  const onPopupOpen = () => {
    setSelectedTags(getValue("tags").selectedTags);
    setFilterType(getValue("tags").filterType);
    setPopupOpen(true);
  };

  const onApply = (e?) => {
    e?.preventDefault();
    if (selectedTags.length > 0) {
      setValue("tags", {
        selectedTags,
        initialTags: props.initialTags,
        filterType
      });
    } else {
      setValue("tags", reset());
    }
    setPopupOpen(false);
  };

  const onToggle = (tag: server.dto.TagSmall) => {
    if (selectedTags.some(s => s.tagId === tag.tagId)) {
      setSelectedTags(selectedTags.filter(s => s.tagId !== tag.tagId));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const onSwitchToWithTag = () => { setFilterType("in"); };
  const onSwitchToWithoutTag = () => { setFilterType("notIn"); };

  const selectedTagsSet = new Set(selectedTags.map(s => s.tagId));

  const tagsExist = props.initialTags.length > 0;
  const [firstColumn, secondColumn] = splitIntoTwoColumns(props.initialTags);

  return <div>
    <FilterPopup
      filterButtonLabel={RESX.Tag.resxTag}
      content={<form onSubmit={onApply}>
        <div className="margin-bottom">
          <ButtonGroup.Group>
            <ButtonGroup.Button
              group="withOrWithoutTag"
              active={filterType === "in"}
              onClick={onSwitchToWithTag}
              type="secondary"
              otherProps={{
                ref: focusOnMount
              }}
            >
              {RESX.Tag.WithTag}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="withOrWithoutTag"
              active={filterType === "notIn"}
              onClick={onSwitchToWithoutTag}
              type="secondary"
            >
              {RESX.Tag.WithoutTag}
            </ButtonGroup.Button>
          </ButtonGroup.Group>
        </div>
        {tagsExist
          ? (
            <>
              <div className="flex gap margin-bottom scrollbox-auto" style={{
                maxHeight: 300,
              }}>
                <div style={{ flexGrow: 1 }}>
                  {firstColumn.map(tag => (
                    <TagRow
                      key={tag.tagId}
                      selected={selectedTagsSet.has(tag.tagId)}
                      onToggle={() => { onToggle(tag); }}
                      tag={tag}
                    />
                  ))}
                </div>
                <div style={{ flexGrow: 1 }}>
                  {secondColumn.map(tag => (
                    <TagRow
                      key={tag.tagId}
                      selected={selectedTagsSet.has(tag.tagId)}
                      onToggle={() => { onToggle(tag); }}
                      tag={tag}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Button primary fluid onClick={onApply}>{RESX.SelectionManager.Apply}</Button>
              </div>
            </>)
          : (
            <Message color="blue">
              <div className="flex">
                <div className="grow1">
                  {RESX.GeneralLabels.Configure}
                </div>
                <div>
                  <a href={routes.tag_New(props.mainArea).absolute}><i className="icon-cog" /></a>
                </div>
              </div>
            </Message>
          )}
      </form>}
      width={700}
      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

interface TagRowProps {
  tag: server.dto.TagSmall
  selected: boolean
  onToggle: () => void
}

export const TagRow = (props: TagRowProps) => <div className="margin-bottom-small">
  <Checkbox
    onChange={props.onToggle}
    checked={props.selected}
    label={<Tag color={props.tag.hexColor} label={props.tag.title} />}
    keyboardNavigationOptions={VerticalKeyboardNavigationGroup("tagFilter")}
  />
</div>;

export default TagFilter;
